import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Box, Button, Container, Grid} from '@mui/material';
import {Skeleton} from '@mui/lab';
import {PageTopActions} from '../PageTopActions';
import PageHeadline from '../../components/PageHeadline';
import AccessControl, {UserPermissions} from '../../components/shared/AccessControl';
import {ResponseListWrapper} from '../../services/model/ResponseListWrapper';
import {FeatureName} from '../../../paths';
import {PageStickyHeader} from '../PageStickyHeader';
import {useTableColumns} from '../UseTableColumns';
import {usePermissions, UsePermissionState} from '../UsePermissions';
import DataGridCustom from '../../components/DataGridCustom';
import {TestAttributes} from '../../TestAttributes';
import SpartanSnackbar from '../../components/SpartanSnackbar';
import {useAxiosContext} from '../../context/AxiosContext';
import {useLoading} from '../../context/LoadingContext';
import {AIAgentTriggerList} from '../../model/AIAgentTrigger';
import {NotificationType} from '../../services/NotificationService';

function AIAgentTriggerListPage() {
  const {useAxiosAICallAPI} = useAxiosContext();
  const {t} = useTranslation();
  const [triggers, setTriggers] = useState<Array<AIAgentTriggerList> | null>(null);
  const {userPermissions}: UsePermissionState = usePermissions(FeatureName.AI_AGENT_TRIGGERS);
  const {setLoading} = useLoading();

  const [{response: getListTriggersResponse, loading: isEntityListLoading, error: hasEntityListError}] =
    useAxiosAICallAPI<ResponseListWrapper<AIAgentTriggerList>>(
      {
        url: `/${FeatureName.AI_AGENT_TRIGGERS}`,
      },
      {manual: false}
    );

  useEffect(() => {
    setLoading(isEntityListLoading, 'AIAgentTriggerListPage');
  }, [isEntityListLoading]);

  const tableColumns = useTableColumns({
    featureName: FeatureName.AI_AGENT_TRIGGERS,
    defaultActionColumn: true,
  });

  useEffect(() => {
    if (getListTriggersResponse) {
      setTriggers(getListTriggersResponse?.data?.results || []);
    }
  }, [getListTriggersResponse]);

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100%',
        py: 3,
      }}
    >
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          {hasEntityListError && (
            <SpartanSnackbar type={NotificationType.ERROR} message={hasEntityListError.message}></SpartanSnackbar>
          )}
          <PageStickyHeader>
            <Grid container item xs={12} rowSpacing={{xs: 3, sm: 3}}>
              <Grid item xs={12} md={6} sx={{display: 'flex', alignItems: 'center'}}>
                <PageHeadline>{t('ai-agent-triggers.headline')}</PageHeadline>
              </Grid>
              <Grid item xs={12} md={6}>
                <PageTopActions>
                  <AccessControl userPermissions={userPermissions} allowedPermissions={[UserPermissions.CREATE]}>
                    <Link to="/ai-agent-triggers/create">
                      <Button fullWidth color="secondary" {...{[TestAttributes.BUTTON_NAME]: 'add-btn'}}>
                        {t('globalActions.addButton')}
                      </Button>
                    </Link>
                  </AccessControl>
                </PageTopActions>
              </Grid>
            </Grid>
          </PageStickyHeader>

          <Grid item xs={12} sx={{height: '750px'}} {...{[TestAttributes.TABLE_NAME]: 'table-results'}}>
            {isEntityListLoading ? (
              <Skeleton variant="rectangular" height={500} />
            ) : (
              <DataGridCustom
                tableColumns={tableColumns}
                rows={
                  triggers?.map((trigger: AIAgentTriggerList, index: number) => {
                    return {
                      ...trigger,
                      actions: trigger.ai_agent_trigger_id,
                      id: index,
                    };
                  }) || []
                }
                userPermissions={userPermissions}
              />
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default AIAgentTriggerListPage;
